<script setup lang="ts">
import BaseButton from '@/components/base/BaseButton.vue'
import BaseIcon from '@/components/base/BaseIcon.vue'
import { useToast } from '@/composables/toast/use-toast.js'

/**
 * The live region. `aria-live="polite"` so an outcome is announced after the current
 * sentence, not over it; `role="status"` per toast. Positioned bottom-right, away from the
 * header actions an operator is about to click next.
 */
const { toasts, dismiss } = useToast()

const TONES = {
  ok: { glyph: 'check-circle', word: 'Done', classes: 'border-ok bg-ok-tint text-ok-on-tint' },
  info: { glyph: 'clipboard-text', word: 'Note', classes: 'border-info bg-info-tint text-info-on-tint' },
  warn: { glyph: 'warning', word: 'Warning', classes: 'border-warn bg-warn-tint text-warn-on-tint' },
  danger: { glyph: 'x-circle', word: 'Error', classes: 'border-danger bg-danger-tint text-danger-on-tint' },
} as const
</script>

<template>
  <div
    class="pointer-events-none fixed right-4 bottom-4 z-50 flex w-full max-w-sm flex-col gap-2"
    aria-live="polite"
  >
    <TransitionGroup name="toast">
      <div
        v-for="toast in toasts"
        :key="toast.id"
        role="status"
        :class="['pointer-events-auto flex items-start gap-2.5 rounded-md border px-3 py-2.5 shadow-overlay', TONES[toast.tone].classes]"
      >
        <BaseIcon
          :name="TONES[toast.tone].glyph"
          :size="18"
          weight="fill"
          class="mt-0.5 shrink-0"
        />
        <div class="min-w-0 flex-1">
          <span class="sr-only">{{ TONES[toast.tone].word }}: </span>
          <p class="text-body font-medium">{{ toast.title }}</p>
          <p
            v-if="toast.body !== undefined"
            class="text-caption opacity-90"
          >
            {{ toast.body }}
          </p>
        </div>
        <BaseButton
          variant="ghost"
          aria-label="Dismiss"
          @click="dismiss(toast.id)"
        >
          <BaseIcon
            name="x-circle"
            :size="16"
          />
        </BaseButton>
      </div>
    </TransitionGroup>
  </div>
</template>

<style scoped>
.toast-enter-active,
.toast-leave-active {
  transition:
    opacity var(--motion-overlay) var(--motion-ease),
    transform var(--motion-overlay) var(--motion-ease);
}
.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateY(6px);
}
</style>
